/**
 * `next/router` shim for the Vite + RR7 frontend.
 *
 * Maps Next's pages-router useRouter() API onto React Router 7 hooks
 * (useNavigate, useLocation, useParams, useSearchParams). Covers the
 * properties and methods the legacy code actually reads:
 *   pathname, asPath, query, push, replace, back, prefetch, events,
 *   isReady, locale.
 *
 * Event subscriptions (routeChangeStart/Complete/Error) are emulated
 * with a thin internal pub-sub that fires when the location changes.
 * This preserves the global loading-bar UX from _app.tsx.
 */
import { useEffect, useMemo } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router';

type EventName =
  | 'routeChangeStart'
  | 'routeChangeComplete'
  | 'routeChangeError'
  | 'hashChangeStart'
  | 'hashChangeComplete'
  | 'beforeHistoryChange';

type EventHandler = (url: string, options?: { shallow?: boolean }) => void;

interface RouterEventBus {
  on(event: EventName, handler: EventHandler): void;
  off(event: EventName, handler: EventHandler): void;
  emit(event: EventName, url: string, options?: { shallow?: boolean }): void;
}

const listeners = new Map<EventName, Set<EventHandler>>();

const events: RouterEventBus = {
  on(event, handler) {
    if (!listeners.has(event)) listeners.set(event, new Set());
    listeners.get(event)!.add(handler);
  },
  off(event, handler) {
    listeners.get(event)?.delete(handler);
  },
  emit(event, url, options) {
    listeners.get(event)?.forEach((handler) => handler(url, options));
  },
};

export interface NextRouter {
  pathname: string;
  asPath: string;
  query: Record<string, string | string[] | undefined>;
  isReady: boolean;
  push: (
    url: string | { pathname?: string; query?: Record<string, unknown> },
    as?: string,
    options?: { shallow?: boolean; scroll?: boolean }
  ) => Promise<boolean>;
  replace: (
    url: string | { pathname?: string; query?: Record<string, unknown> },
    as?: string,
    options?: { shallow?: boolean; scroll?: boolean }
  ) => Promise<boolean>;
  back: () => void;
  reload: () => void;
  prefetch: (url: string) => Promise<void>;
  events: RouterEventBus;
  locale?: string;
}

let lastUrl: string | null = null;

function urlObjectToString(input: {
  pathname?: string;
  query?: Record<string, unknown>;
}): string {
  const pathname = input.pathname ?? '/';
  const params = new URLSearchParams();
  if (input.query) {
    Object.entries(input.query).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        value.forEach((v) => params.append(key, String(v)));
      } else {
        params.set(key, String(value));
      }
    });
  }
  const qs = params.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

export function useRouter(): NextRouter {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const fullPath = useMemo(
    () => `${location.pathname}${location.search}${location.hash}`,
    [location.pathname, location.search, location.hash]
  );

  // Emit routeChangeComplete on every navigation. routeChangeStart fires
  // pre-navigation in Next; we don't have that fine-grained hook here, so
  // listeners that only need a "navigation happened" signal still work.
  useEffect(() => {
    if (lastUrl !== null && lastUrl !== fullPath) {
      events.emit('routeChangeComplete', fullPath);
    }
    lastUrl = fullPath;
  }, [fullPath]);

  const query: Record<string, string | string[]> = useMemo(() => {
    const out: Record<string, string | string[]> = {};
    searchParams.forEach((value, key) => {
      const existing = out[key];
      if (existing === undefined) {
        out[key] = value;
      } else if (Array.isArray(existing)) {
        existing.push(value);
      } else {
        out[key] = [existing, value];
      }
    });
    // Path params from RR7 are also exposed as query, matching Next's
    // behavior where dynamic-segment values appear in router.query.
    Object.entries(params).forEach(([key, value]) => {
      if (typeof value === 'string') out[key] = value;
    });
    return out;
  }, [searchParams, params]);

  return useMemo<NextRouter>(
    () => ({
      pathname: location.pathname,
      asPath: fullPath,
      query,
      isReady: true,
      push: async (url) => {
        const target = typeof url === 'string' ? url : urlObjectToString(url);
        events.emit('routeChangeStart', target);
        navigate(target);
        return true;
      },
      replace: async (url) => {
        const target = typeof url === 'string' ? url : urlObjectToString(url);
        events.emit('routeChangeStart', target);
        navigate(target, { replace: true });
        return true;
      },
      back: () => {
        navigate(-1);
      },
      reload: () => {
        if (typeof window !== 'undefined') window.location.reload();
      },
      prefetch: async () => {
        // RR7 prefetches via <Link prefetch="intent"> on the Link itself.
        // Programmatic prefetch is a no-op in this shim.
      },
      events,
    }),
    [location.pathname, fullPath, query, navigate]
  );
}

export default { useRouter, events };
